import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png"; // Update with your logo path

function AuthorSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [authorsList, setAuthorsList] = useState([]);
  const [filteredAuthorsList, setFilteredAuthorsList] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      name: "",
      bio: "",
      createdBy: localStorage.getItem("userId"),
    },
  });

  const getAuthorsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(`LMS/GetAuthorAsync`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAuthorsList(response?.authorsService || []);
      setFilteredAuthorsList(response?.authorsService || []);
    } catch (error) {
      console.error("Error fetching authors:", error);
    }
  };

  useEffect(() => {
    getAuthorsList();
  }, [triggerRerender]);

  const handleEdit = async (authorId) => {
    try {
      const response = await get(
        `LMS/GetAuthorByIDAsync?authorId=${authorId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { authorsService: singleAuthorData } = response;

      reset({
        ...singleAuthorData,
        id: singleAuthorData.id,
        updatedBy: localStorage.getItem("userId"),
      });
    } catch (error) {
      console.error("Error editing author:", error);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Name",
        // width: "150px",
      },
      {
        accessorKey: "bio",
        header: "Bio",
        // width: "250px",
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const dataToExport = filteredAuthorsList.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Authors");
    XLSX.writeFile(workbook, "Authors.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Authors List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = filteredAuthorsList.map((row, index) => {
      return [
        index + 1,
        row.name,
        row.bio,
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("AuthorsList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post("LMS/AddUpdateAuthorAsync", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          name: "",
          bio: "",
          createdBy: localStorage.getItem("userId"),
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  useEffect(() => {
    const filtered = authorsList.filter((author) => {
      const searchLower = searchTerm.toLowerCase();
      return author.name.toLowerCase().includes(searchLower);
    });
    setFilteredAuthorsList(filtered);
  }, [searchTerm, authorsList]);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Authors</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="col-md-8">
                  <label className="form-label form-label-new">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`name`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("name")}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                {/* <div className="m-0"></div> */}
                <div className="col-md-8">
                  <label className="form-label form-label-new">Bio</label>
                  <textarea
                    className="form-control"
                    {...register(`bio`, {
                      maxLength: {
                        value: 250,
                        message: "Maximum of 250 characters allowed.",
                      },
                    })}
                    onChange={handleChangeOfInputWithLengthCheck("bio")}
                  ></textarea>

                  {errors.bio && (
                    <small className="text-danger">{errors.bio.message}</small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={filteredAuthorsList}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                    columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthorSetup;
